<script lang="ts" setup>
import { useLanguage } from '@/composables/useLanguage';
import { getCultureFromCode } from '@sysarb/sysarb-types/general/locales';
import type { ICellRendererParams } from 'ag-grid-community';
import type { PropType} from 'vue';
import { computed } from 'vue';

const { language } = useLanguage();

type ExtendedParams = ICellRendererParams & {
    total: (params: ICellRendererParams) => number | null;
    valuesToCompound: (params: ICellRendererParams) => { title: string; value: number}[];
    currency: (params: ICellRendererParams) => string;
    decimals: (params: ICellRendererParams) => number;
};

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

const totalOverride = computed(() => (typeof props.params.total === 'function' ? props.params.total(props.params) : props.params.total));
const valuesToCompound = computed(() => (typeof props.params.valuesToCompound === 'function' ? props.params.valuesToCompound(props.params) : props.params.valuesToCompound));
const currency = computed(() => (typeof props.params.currency === 'function' ? props.params.currency(props.params) : props.params.currency));
const decimals = computed(() => (typeof props.params.decimals === 'function' ? props.params.decimals(props.params) : props.params.decimals));

const localisation = computed<string>(()=> getCultureFromCode(language.value));

function getCompoundValue(): string {
    // return the override value if it exists
    if (totalOverride.value != null) return totalOverride.value.toString();

    // otherwise return the compound value
    const compoundValue: number = valuesToCompound.value.flatMap(x => x.value).reduce((a: number | string, b: number | string) => ((+a) + (+b)));

    return currency.value !== ''
        ? `${Number(compoundValue.toFixed(decimals.value)).toLocaleString(localisation.value)} ${currency.value}`
        : Number(compoundValue.toFixed(decimals.value)).toLocaleString(localisation.value);
}

function getTooltip(): string {
    let tooltip = '';
    valuesToCompound.value.forEach((valueToCompound: any) => {
        let displayValue = valueToCompound.value;
        if (!Number.isNaN(+valueToCompound)) {
            displayValue = Number(valueToCompound.value).toLocaleString(localisation.value);
        }

        tooltip += `<b>${valueToCompound.title}:</b> ${displayValue}\n`;
    }, valuesToCompound);
    return tooltip;
}
</script>

<template>
    <div class="flex-container align-middle align-center height-100">
        {{ getCompoundValue() }}
        <Icon
            v-tooltip="getTooltip()"
            icon="info"
            trigger="hover"
            class="margin-left-3"
        />
    </div>
</template>
